import { useEffect, useState } from 'react';
import { formatDateTime } from './ui.js';

/** Chrome and Edge offer their own install prompt; Safari does not. */
interface InstallPrompt extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

function installed(): boolean {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    (navigator as { standalone?: boolean }).standalone === true
  );
}

/**
 * My account, This device: how to install the portal as an app, and until
 * when it works offline here (the session kept on the device).
 */
export function InstallHelp({ offlineUntil }: { offlineUntil: string }) {
  const [prompt, setPrompt] = useState<InstallPrompt | null>(null);
  const [done, setDone] = useState(installed);

  useEffect(() => {
    const onPrompt = (e: Event) => {
      e.preventDefault();
      setPrompt(e as InstallPrompt);
    };
    const onInstalled = () => setDone(true);
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  async function install() {
    if (!prompt) return;
    await prompt.prompt();
    if ((await prompt.userChoice).outcome === 'accepted') setDone(true);
    setPrompt(null);
  }

  return (
    <section className="section card">
      <h2 className="sec">This device</h2>
      <p>
        Offline use on this device ends on <b>{formatDateTime(offlineUntil)}</b>
      </p>
      <p className="muted small">
        After that, sign in again with the network. Nothing saved on this device is lost.
      </p>
      {done ? (
        <p className="muted small">The portal is installed as an app on this device.</p>
      ) : (
        <>
          <p>
            Install the portal as an app: it opens from the home screen, full screen, even without
            network.
          </p>
          {prompt && (
            <button type="button" className="btn primary" onClick={() => void install()}>
              Install the app
            </button>
          )}
          <ul className="small">
            <li>
              <b>iPhone or iPad (Safari):</b> tap Share, then <i>Add to Home Screen</i>.
            </li>
            <li>
              <b>Android (Chrome):</b> open the menu (three dots), then <i>Install app</i>.
            </li>
            <li>
              <b>Computer (Chrome or Edge):</b> click the install icon at the right of the address
              bar.
            </li>
          </ul>
        </>
      )}
    </section>
  );
}
